import { FileText, Loader2, X } from 'lucide-react';
import { formatFileSize } from '../agent-chat-module/utils/format-file-size';
import { isImageAttachment } from '../utils/assistant-file-preview';
import type { Attachment } from '../types/chat';
import { ImagePreview } from './ImagePreview';

const CHIP_HEIGHT_CLASS = 'h-20 max-h-20 min-h-20';
const IMAGE_SIZE_CLASS = 'h-20 w-20 max-h-20 max-w-20 min-h-20 min-w-20';
const IMAGE_THUMB_CLASS = 'h-full w-full object-cover';

export const formatAttachmentCardMeta = (extension?: string, size?: number) => {
  const ext = (extension || '').replace(/^\./, '').toLowerCase();
  const sizeLabel = formatFileSize(size).toLowerCase();
  return [ext, sizeLabel].filter(Boolean).join(' ');
};

type AttachmentFileCardProps = {
  file: Pick<Attachment, 'fileName' | 'extension' | 'size'>;
  isDark?: boolean;
  onRemove?: () => void;
  isProcessing?: boolean;
  removeLabel?: string;
};

export function AttachmentFileCard({
  file,
  isDark = false,
  onRemove,
  isProcessing = false,
  removeLabel = 'Remove file',
}: AttachmentFileCardProps) {
  const meta = formatAttachmentCardMeta(file.extension, file.size);

  return (
    <div
      className={`relative box-border flex ${CHIP_HEIGHT_CLASS} min-w-0 items-center gap-2 rounded-xl border px-2 ${onRemove || isProcessing ? 'pr-7' : 'pr-2.5'
        } ${isDark ? 'border-[#3a3a3f] bg-[#2a2a2e]' : 'border-[#E5E5E5] bg-[#F7F7F7]'}`}
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md border ${isDark ? 'border-[#3a3a3f] bg-[#323238]' : 'border-[#E5E5E5] bg-white'
          }`}
      >
        <FileText className={`h-4 w-4 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`} />
      </div>
      <div className="min-w-0 min-h-0">
        <p
          className={`truncate text-sm font-medium leading-4 ${isDark ? 'text-true-white' : 'text-neutral-800'}`}
          title={file.fileName}
        >
          {file.fileName}
        </p>
        {meta ? (
          <p className={`truncate text-xs leading-4 ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>
            {meta}
          </p>
        ) : null}
      </div>
      {isProcessing ? (
        <div className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-black text-white shadow-sm">
          <Loader2 className="h-3 w-3 animate-spin" />
        </div>
      ) : onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-black text-white shadow-sm hover:bg-neutral-800"
          aria-label={removeLabel}
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </div>
  );
}

type AttachmentFileCardListProps = {
  files: Attachment[];
  isDark?: boolean;
  onRemove?: (index: number) => void;
  isProcessingFile?: (file: Attachment) => boolean;
  previewUrlById?: Map<string, string>;
};

function AttachmentImageThumb({
  file,
  previewUrl,
  onRemove,
  isProcessing,
}: {
  file: Attachment;
  previewUrl?: string;
  onRemove?: () => void;
  isProcessing?: boolean;
}) {
  return (
    <div className={`relative ${IMAGE_SIZE_CLASS} shrink-0`}>
      <div className="relative h-full w-full overflow-hidden rounded-lg border border-border">
        {previewUrl ? (
          <img className={IMAGE_THUMB_CLASS} src={previewUrl} alt={file.fileName} />
        ) : (
          <ImagePreview itemId={file.fileId} alt={file.fileName} className={IMAGE_THUMB_CLASS} />
        )}
        {isProcessing ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/35">
            <Loader2 className="h-5 w-5 animate-spin text-white" />
          </div>
        ) : null}
      </div>
      {!isProcessing && onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="absolute -right-1.5 -top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-black text-white shadow-sm hover:bg-neutral-800"
          aria-label="Remove image"
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </div>
  );
}

export function AttachmentFileCardList({
  files,
  isDark = false,
  onRemove,
  isProcessingFile,
  previewUrlById,
}: AttachmentFileCardListProps) {
  if (files.length === 0) return null;

  const images: { file: Attachment; index: number }[] = [];
  const documents: { file: Attachment; index: number }[] = [];
  files.forEach((file, index) => {
    if (isImageAttachment(file.fileName, file.extension)) {
      images.push({ file, index });
    } else {
      documents.push({ file, index });
    }
  });

  return (
    <div className="flex flex-col gap-2">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1.5 pr-1.5" data-testid="attachment-image-grid">
          {images.map(({ file, index }) => (
            <AttachmentImageThumb
              key={file.fileId || `${file.fileName}-${index}`}
              file={file}
              previewUrl={previewUrlById?.get(file.fileId)}
              onRemove={onRemove ? () => onRemove(index) : undefined}
              isProcessing={isProcessingFile?.(file)}
            />
          ))}
        </div>
      )}
      {documents.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="attachment-file-list">
          {documents.map(({ file, index }) => (
            <div key={file.fileId || `${file.fileName}-${index}`} className="w-[260px] max-w-full">
              <AttachmentFileCard
                file={file}
                isDark={isDark}
                onRemove={onRemove ? () => onRemove(index) : undefined}
                isProcessing={isProcessingFile?.(file)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function UserMessageAttachments({
  files,
  isDark,
  previewUrlById,
}: {
  files: Attachment[];
  isDark: boolean;
  previewUrlById?: Map<string, string>;
}) {
  if (!files.length) return null;

  const images = files.filter((file) => isImageAttachment(file.fileName, file.extension));
  const documents = files.filter((file) => !isImageAttachment(file.fileName, file.extension));

  return (
    <>
      {images.length > 0 && (
        <div className="flex w-full justify-end" data-testid="user-message-images">
          <AttachmentFileCardList files={images} isDark={isDark} previewUrlById={previewUrlById} />
        </div>
      )}
      {documents.length > 0 && (
        <div className="flex w-full justify-end" data-testid="user-message-files">
          <AttachmentFileCardList
            files={documents}
            isDark={isDark}
            previewUrlById={previewUrlById}
          />
        </div>
      )}
    </>
  );
}
